import {
  CreateLocation,
  CreateOrg,
  Location,
  OrgFullDetail,
  OrgMemberRole,
  SendOrgInvitationRequest,
  UpdateOrg
} from "@garther/interfaces";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiClients } from "./context";

export const orgKeys = {
  all: ["orgs"] as const,
  listByUser: (userId: string) => [...orgKeys.all, "user", userId] as const,
  popular: () => [...orgKeys.all, "popular"] as const,
  pendingInvitations: (orgId: string) => [...orgKeys.all, orgId, "pending-invitations"] as const,
  invitationByToken: (token: string) => [...orgKeys.all, "invitation", token] as const
};

export function useOrgsByUser(userId: string, options: { enabled?: boolean } = {}) {
  const { orgApiClient } = useApiClients();

  return useQuery({
    queryKey: orgKeys.listByUser(userId),
    queryFn: () => orgApiClient.getListByUserId(userId),
    enabled: (options.enabled ?? true) && !!userId,
    staleTime: 5 * 60 * 1000
  });
}

export function usePopularOrgs() {
  const { orgApiClient } = useApiClients();

  return useQuery({
    queryKey: orgKeys.popular(),
    queryFn: () => orgApiClient.getListPopular(),
    staleTime: 10 * 60 * 1000
  });
}

export function useOrgPendingInvitations(orgId: string, options: { enabled?: boolean } = {}) {
  const { orgApiClient } = useApiClients();

  return useQuery({
    queryKey: orgKeys.pendingInvitations(orgId),
    queryFn: () => orgApiClient.getPendingInvitations(orgId),
    enabled: (options.enabled ?? true) && !!orgId,
    staleTime: 30 * 1000
  });
}

export function useCreateOrgMutation(
  options: { onSuccess?: (result: OrgFullDetail) => void; onError?: (error: unknown) => void } = {}
) {
  const { orgApiClient } = useApiClients();
  return useMutation({
    mutationFn: (data: CreateOrg) => orgApiClient.create(data),
    onSuccess: (result) => {
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useUpdateOrgMutation(
  options: { onSuccess?: (result: OrgFullDetail) => void; onError?: (error: unknown) => void } = {}
) {
  const { orgApiClient } = useApiClients();
  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: UpdateOrg }) => orgApiClient.update(orgId, data),
    onSuccess: (result) => {
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useUpdateOrgMemberRoleMutation(
  options: { onSuccess?: () => void; onError?: (error: unknown) => void } = {}
) {
  const { orgApiClient } = useApiClients();
  return useMutation({
    mutationFn: ({ orgId, userId, role }: { orgId: string; userId: string; role: OrgMemberRole }) =>
      orgApiClient.updateMemberRole(orgId, userId, role),
    onSuccess: () => {
      options.onSuccess?.();
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useRemoveOrgMemberMutation(
  options: { onSuccess?: () => void; onError?: (error: unknown) => void } = {}
) {
  const { orgApiClient } = useApiClients();
  return useMutation({
    mutationFn: ({ orgId, userId }: { orgId: string; userId: string }) =>
      orgApiClient.removeOrgMember(orgId, userId),
    onSuccess: () => {
      options.onSuccess?.();
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useCreateOrgLocationMutation(
  options: { onSuccess?: (result: Location) => void; onError?: (error: unknown) => void } = {}
) {
  const { orgApiClient } = useApiClients();
  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: CreateLocation }) =>
      orgApiClient.createLocation(orgId, data),
    onSuccess: (result) => {
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useDeleteOrgLocationMutation(
  options: { onSuccess?: () => void; onError?: (error: unknown) => void } = {}
) {
  const { orgApiClient } = useApiClients();
  return useMutation({
    mutationFn: ({ orgId, locationId }: { orgId: string; locationId: string }) =>
      orgApiClient.deleteLocation(orgId, locationId),
    onSuccess: () => {
      options.onSuccess?.();
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useSendOrgInvitationMutation(
  options: { onSuccess?: () => void; onError?: (error: unknown) => void } = {}
) {
  const { orgApiClient } = useApiClients();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: SendOrgInvitationRequest }) =>
      orgApiClient.sendInvitation(orgId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: orgKeys.pendingInvitations(variables.orgId) });
      options.onSuccess?.();
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useAcceptOrgInvitationMutation(
  options: { onSuccess?: () => void; onError?: (error: unknown) => void } = {}
) {
  const { orgApiClient } = useApiClients();
  return useMutation({
    mutationFn: (token: string) => orgApiClient.acceptOrgInvitation(token),
    onSuccess: () => {
      options.onSuccess?.();
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useOrgInvitationByToken(token: string, options: { enabled?: boolean } = {}) {
  const { orgApiClient } = useApiClients();

  return useQuery({
    queryKey: orgKeys.invitationByToken(token),
    queryFn: () => orgApiClient.getOrgInvitation(token),
    enabled: (options.enabled ?? true) && !!token,
    staleTime: 60 * 1000
  });
}
